import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../design-system/components/composite/Modal/Modal";
import { Spinner, TableSelectRow } from "../../atoms";
import Pagination from "../../molecules/Pagination/Pagination";
import { Button } from "../../../design-system/components/primitives/Button/Button";
import { connectorDetailService } from "../../../services/connectors/connectorDetail.service";
import {
  listConnectorTableAssets,
  type ConnectorTableRow,
} from "../../../services/connectors/connectorAssetTree.service";
import {
  connectorAssetsApi,
  connectorPreviewCountsApi,
} from "../../../services/api";
import { rawColors } from "../../../design-system/tokens/colors";
import {
  CenteredState,
  ErrorText,
  FooterBar,
  HeaderTitle,
  ListScroll,
  ModalBodyFrame,
  ModalSubtitle,
  TableCard,
} from "./EditDatasetModal.styles";
import type { EditDatasetModalProps } from "./EditDatasetModal.types";

type LoadState = "idle" | "loading" | "ready" | "error";

type DraftRow = ConnectorTableRow & { draftSelected: boolean };

// Google Sheets only (see effect below) — paginating avoids ever needing to
// fetch counts for more than a page's worth of unselected sheets at once,
// which is the whole reason Sheets never fetched all-at-once like
// Salesforce's chunked refresh does (Sheets' 60-calls/min quota is far
// tighter relative to a large Drive's file count). Other connectors are
// untouched — this constant and the pagination UI only ever apply when
// catalogKey === "google_sheets".
const SHEETS_PAGE_SIZE = 10;

const SaveActionButton = styled(Button)`
  && {
  background: ${rawColors.primary[500]};
  border-color: ${rawColors.primary[500]};
    &:hover{
     background: ${rawColors.primary[500]};
      border-color: ${rawColors.primary[500]};
    }
    &:disabled {
      background: ${rawColors.primary[200]};
      border-color: ${rawColors.primary[200]};
      color: ${rawColors.text.white};
    }
  }
`;

export const EditDatasetModal: React.FC<EditDatasetModalProps> = ({
  open,
  onClose,
  connectionId,
  catalogKey,
  onSaved,
}) => {
  const [loadState, setLoadState] = useState<LoadState>("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [draftRows, setDraftRows] = useState<DraftRow[]>([]);
  const [highlightedAssetId, setHighlightedAssetId] = useState<string | null>(
    null,
  );
  const [isSaving, setIsSaving] = useState(false);
  // Sheets-only pagination state — unused (and never rendered) for every
  // other connector, which keeps its existing full-list behavior exactly
  // as-is.
  const [sheetsPage, setSheetsPage] = useState(1);
  // Sheets-only: true while the *current page's* counts are being fetched —
  // drives a full-list loading state (spinner in place of the rows) rather
  // than a per-row indicator, since the whole page's numbers land together.
  const [pageCountsLoading, setPageCountsLoading] = useState(false);
  const isGoogleSheets = catalogKey === "google_sheets";

  const resetLocalState = useCallback(() => {
    setLoadState("idle");
    setLoadError(null);
    setSaveError(null);
    setDraftRows([]);
    setHighlightedAssetId(null);
    setIsSaving(false);
    setSheetsPage(1);
    setPageCountsLoading(false);
  }, []);

  useEffect(() => {
    if (!open) {
      resetLocalState();
      return;
    }

    let cancelled = false;
    const run = async () => {
      setLoadState("loading");
      setLoadError(null);
      setSaveError(null);
      try {
        const rows = await listConnectorTableAssets({
          connectionId,
          catalogKey,
        });
        if (cancelled) return;
        setDraftRows(
          rows.map((row) => ({
            ...row,
            draftSelected: row.isSelected,
          })),
        );
        setHighlightedAssetId(rows[0]?.assetId ?? null);
        setLoadState("ready");

        // Persisted row/column counts are 0 for unselected Salesforce objects
        // (fields are only discovered for selected objects — see
        // SALESFORCE_CONNECTOR_TECHNICAL_GUIDE.md). Refresh every listed
        // object's counts on-demand via the same read-only preview-counts
        // call the wizard's Preview step uses (no DB writes, no field
        // persistence, so no KG growth) so the modal always shows real
        // numbers. Chunked and run with limited concurrency so counts fill
        // in progressively as each chunk resolves, instead of one ~20s
        // request for the whole org before anything updates.
        if (catalogKey === "salesforce") {
          const names = rows.map((row) => row.table);
          const CHUNK_SIZE = 50;
          const CONCURRENCY = 4;
          const chunks: string[][] = [];
          for (let i = 0; i < names.length; i += CHUNK_SIZE) {
            chunks.push(names.slice(i, i + CHUNK_SIZE));
          }

          const applyChunk = async (chunk: string[]) => {
            if (cancelled) return;
            try {
              const { counts } = await connectorPreviewCountsApi.fetch(
                connectionId,
                chunk,
              );
              if (cancelled) return;
              setDraftRows((prev) =>
                prev.map((row) => {
                  const c = counts[row.table];
                  if (!c) return row;
                  return {
                    ...row,
                    rowCount: c.row_count ?? row.rowCount,
                    columnCount: c.column_count ?? row.columnCount,
                  };
                }),
              );
            } catch {
              // Best-effort refresh — keep the already-loaded persisted
              // counts for this chunk rather than failing the whole modal.
            }
          };

          let nextIndex = 0;
          const worker = async () => {
            while (!cancelled) {
              const idx = nextIndex++;
              if (idx >= chunks.length) return;
              await applyChunk(chunks[idx]);
            }
          };
          await Promise.all(
            Array.from({ length: Math.min(CONCURRENCY, chunks.length) }, worker),
          );
        }
      } catch {
        if (cancelled) return;
        setLoadError("Could not load tables. Please try again.");
        setLoadState("error");
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [open, connectionId, catalogKey, resetLocalState]);

  // Sheets-only: fetch real counts for whichever page is currently visible,
  // skipping any row that already has a real count (already fetched via the
  // wizard's Preview step, a previous page view, or a background refresh) —
  // never re-fetches, never touches rows on other pages. This is the
  // pagination + skip-if-known design agreed for Sheets specifically;
  // Salesforce keeps its existing full-org chunked refresh above, untouched.
  useEffect(() => {
    if (!open || !isGoogleSheets || loadState !== "ready") return;

    const pageRows = draftRows.slice(
      (sheetsPage - 1) * SHEETS_PAGE_SIZE,
      sheetsPage * SHEETS_PAGE_SIZE,
    );
    const needsFetch = pageRows.filter((row) => row.rowCount == null);
    if (needsFetch.length === 0) {
      setPageCountsLoading(false);
      return;
    }

    const needsFetchIds = needsFetch.map((row) => row.assetId);
    let cancelled = false;
    setPageCountsLoading(true);

    const run = async () => {
      try {
        const { counts } = await connectorPreviewCountsApi.fetchByAssetIds(
          connectionId,
          needsFetchIds,
        );
        if (cancelled) return;
        setDraftRows((prev) =>
          prev.map((row) => {
            const c = counts[row.assetId];
            if (!c) return row;
            return {
              ...row,
              rowCount: c.row_count ?? row.rowCount,
              columnCount: c.column_count ?? row.columnCount,
            };
          }),
        );
      } catch {
        // Best-effort — leave this page's rows showing "—" for whichever
        // ones failed rather than blocking the modal.
      } finally {
        if (!cancelled) setPageCountsLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isGoogleSheets, loadState, sheetsPage, connectionId]);

  const hasChanges = useMemo(
    () => draftRows.some((row) => row.draftSelected !== row.isSelected),
    [draftRows],
  );

  // Sheets-only slicing — every other connector renders `draftRows` in
  // full, exactly as before this change.
  const sheetsPageCount = isGoogleSheets
    ? Math.max(1, Math.ceil(draftRows.length / SHEETS_PAGE_SIZE))
    : 1;
  const visibleRows = isGoogleSheets
    ? draftRows.slice(
        (sheetsPage - 1) * SHEETS_PAGE_SIZE,
        sheetsPage * SHEETS_PAGE_SIZE,
      )
    : draftRows;

  const setRowSelected = useCallback((assetId: string, next: boolean) => {
    setDraftRows((prev) =>
      prev.map((row) =>
        row.assetId === assetId ? { ...row, draftSelected: next } : row,
      ),
    );
  }, []);

  const handleSave = useCallback(async () => {
    if (!catalogKey.trim() || isSaving) return;
    const updates = draftRows.filter(
      (row) => row.draftSelected !== row.isSelected,
    );
    if (updates.length === 0) {
      onClose();
      return;
    }
    setSaveError(null);
    setIsSaving(true);
    try {
      const toSelect = updates
        .filter((row) => row.draftSelected)
        .map((row) => row.assetId);
      const toDeselect = updates.filter((row) => !row.draftSelected);

      await Promise.all([
        toSelect.length > 0
          ? connectorDetailService.patchAssetSelection(catalogKey, toSelect)
          : Promise.resolve(),
        ...toDeselect.map((row) =>
          connectorAssetsApi.patch(catalogKey, row.assetId, {
            is_selected: false,
          }),
        ),
      ]);
      onSaved?.();
      onClose();
    } catch {
      setSaveError("Could not save selection. Please try again.");
    } finally {
      setIsSaving(false);
    }
  }, [catalogKey, draftRows, isSaving, onClose, onSaved]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      closable={!isSaving}
      closeOnBackdropClick={!isSaving}
      closeOnEscape={!isSaving}
    >
      <ModalHeader onClose={onClose}>
        <div>
          <HeaderTitle>Edit connected dataset</HeaderTitle>
          <ModalSubtitle>
            Select which tables to include in this connection. Changes apply
            when you save.
          </ModalSubtitle>
        </div>
      </ModalHeader>

      <ModalBody overflowY="hidden">
        <ModalBodyFrame>
          {saveError ? <ErrorText role="alert">{saveError}</ErrorText> : null}
          {loadState === "loading" ? (
            <CenteredState role="status">
              <Spinner />
              <span>Loading tables…</span>
            </CenteredState>
          ) : null}
          {loadState === "error" ? (
            <CenteredState role="alert">{loadError}</CenteredState>
          ) : null}
          {loadState === "ready" && draftRows.length === 0 ? (
            <CenteredState>No tables found for this connection.</CenteredState>
          ) : null}
          {loadState === "ready" && draftRows.length > 0 ? (
            <TableCard>
              {isGoogleSheets && pageCountsLoading ? (
                <CenteredState role="status">
                  <Spinner />
                  <span>Loading counts…</span>
                </CenteredState>
              ) : (
                <ListScroll>
                  {visibleRows.map((row) => (
                    <TableSelectRow
                      key={row.assetId}
                      name={row.schema ? `${row.schema}.${row.table}` : row.table}
                      rowCount={row.rowCount}
                      columnCount={row.columnCount}
                      checked={row.draftSelected}
                      highlighted={highlightedAssetId === row.assetId}
                      onToggleCheck={(next) => setRowSelected(row.assetId, next)}
                      onRowClick={() => setHighlightedAssetId(row.assetId)}
                    />
                  ))}
                </ListScroll>
              )}
              {isGoogleSheets ? (
                <Pagination
                  page={sheetsPage}
                  pageCount={sheetsPageCount}
                  pageSize={SHEETS_PAGE_SIZE}
                  totalCount={draftRows.length}
                  onPageChange={setSheetsPage}
                />
              ) : null}
            </TableCard>
          ) : null}
        </ModalBodyFrame>
      </ModalBody>

      <ModalFooter>
        <FooterBar>
          <Button
            variant="link-pill"
            size="md"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <SaveActionButton
            variant="primary"
            size="md"
            onClick={() => void handleSave()}
            disabled={isSaving || loadState !== "ready" || !hasChanges}
          >
            {isSaving ? "Saving…" : "Save"}
          </SaveActionButton>
        </FooterBar>
      </ModalFooter>
    </Modal>
  );
};

export default EditDatasetModal;
